import { ComponentType, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { useSnapshot } from "valtio";
import clsx from "clsx";
import { LayerTypeControlPageProps } from "../../types";
import Feature from "src/js/stores/user/Feature";
import { useControlTracking } from "../../ControlTrackingContext";
import { mapStore } from "src/js/stores/map/store";
import { comparablesStore } from "src/js/stores/comparables";
import { getMeasurementSystemPreference } from "src/js/stores/user/actions/getSetting";
import {
  setPriceType,
  setPriceMode,
  setDataSource,
  setFilters,
  setHmlrFilters,
  setHometrackFilters,
  setPriceSource,
  setIncNoFloorArea,
} from "src/js/stores/comparables/actions";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import { PriceMode } from "react-migration/domains/comparables/components/Visualisations/PriceMode";
import { FiltersWithClear } from "react-migration/domains/comparables/components/Filters/FiltersWithClear";
import { PriceType } from "react-migration/domains/comparables/components/Visualisations/PriceType";
import { ComparablesDataSourceSwitch } from "react-migration/layouts/comparables/ComparablesDataSourceSwitch";
import hasBetaFeature from "src/js/stores/user/actions/hasBetaFeature";
import { HometrackUpgradeCTA } from "./HometrackUpgradeCTA";
import { CalculatorActions } from "react-migration/domains/comparables/components/Calculator/Actions";
import { userStore } from "src/js/stores/user/store";
import { isZoopla } from "react-migration/domains/comparables/util/isZoopla";
import {
  disabledEPCFields,
  disabledEPCFieldsUS,
  disabledZooplaFields,
} from "react-migration/domains/comparables/util/DisabledFilters";
import { getCombinedDisabledFields } from "react-migration/domains/comparables/util/getCombinedDisabledFields";
import { useDebounce } from "usehooks-ts";
import { PriceModes } from "react-migration/domains/comparables/typings/PriceModes";
import area from "@turf/area";
import { googleBoundsToEsBounds, googleBoundsToGeoJsonPoly } from "src/js/util/map_util";
import { drawingStore } from "src/js/stores/drawing/store";
import {
  IComparableSearchArgsGQL,
  IPolygonSearch,
  PropertySubCategoryGQL,
} from "react-migration/lib/typings/comparables";
import { DrawingItem } from "src/js/types/DrawingItem";
import { getSelectedGeometry } from "src/js/stores/drawing/actions/getSelectedGeometry";
import { subscribeToDrawingStore } from "src/js/stores/drawing/actions/subscribeToDrawingStore";
import { convertGeoJsonToPolygonGeoFilter } from "react-migration/domains/comparables/util/Geo";
import { useComparableSearch } from "react-migration/domains/comparables/hooks/useComparableSearch";
import { buildCompsQuery } from "react-migration/domains/comparables/util/BuildCompsQuery";
import {
  currentValueAverage,
  lastValueAverage,
  validValueResults,
} from "react-migration/domains/comparables/util/ZooplaPrices";
import { CalculatorResultTypes } from "react-migration/domains/comparables/typings/ResultTypes";
import {
  marketValueAverage,
  soldPriceAverage,
  validPriceResults,
} from "react-migration/domains/comparables/util/Prices";
import { ZooplaComparableDTO } from "react-migration/lib/typings/Zoopla";
import { RecordType } from "react-migration/domains/comparables/typings/Record";
import { ComparablesCalculatorErrorMessage } from "react-migration/domains/comparables/components/Calculator/ComparablesCalculatorErrorMessage/ComparablesCalculatorErrorMessage";
import { CalculatorResults } from "react-migration/domains/comparables/components/Calculator/Results";
import { Suspense } from "react";
import { Accordion } from "react-migration/components/Accordion";
import { Spinner } from "react-migration/components/Spinner/Spinner";
import { getComparablesDrawingId } from "src/js/stores/drawing/actions/getComparablesDrawingId";
import { deleteDrawing } from "src/js/stores/drawing/actions/deleteDrawing";

const ComparablesControlPageDrawings = lazy(() => import("./ComparablesControlPageDrawings"));

const empty: unknown[] = [];

export const ComparablesControlPage: ComponentType<LayerTypeControlPageProps> = ({ disabled }) => {
  const unitPreference = getMeasurementSystemPreference();
  const { debouncedTrackControlChange, trackControlChange } = useControlTracking();
  const { locale } = useSnapshot(userStore);
  const { hmlrFilters, hometrackFilters, priceMode, priceType, incNoFloorArea, dataSource, priceSource, filters } =
    useSnapshot(comparablesStore);
  const { mapBounds, mapX, mapY, zoom } = useSnapshot(mapStore.settings);
  const bounds = useDebounce(mapBounds, 1000);

  const [polygonGeometry, setPolygonGeometry] = useState<IPolygonSearch | null>(null);
  const [drawing, setDrawing] = useState<DrawingItem | undefined>(undefined);
  const [searchPoly, setSearchPoly] = useState<IComparableSearchArgsGQL>();
  const [startDrawing, setStartDrawing] = useState<boolean>();
  const [isTooLarge, setIsTooLarge] = useState<boolean>(false);

  const useHometrack =
    (hasBetaFeature(Feature.hometrackv2) || hasFeature(Feature.zooplaFullAccess)) && !hasFeature(Feature.usAccess);
  const isZooplaData = isZoopla(priceMode);
  const disabledFields = getCombinedDisabledFields(
    isZooplaData ? disabledZooplaFields : hasFeature(Feature.usAccess) ? disabledEPCFieldsUS : disabledEPCFields
  );
  // Determine which filter state to use based on current data source
  const showUpgradeCta = dataSource === "hometrack" && !hasFeature(Feature.zooplaFullAccess);

  const currentFilters = useMemo(() => {
    if (!useHometrack) {
      return filters;
    }
    return dataSource === "hmlr" ? hmlrFilters : hometrackFilters;
  }, [dataSource, hmlrFilters, hometrackFilters, useHometrack, filters]);

  function validateDrawingSize(priceMode: PriceModes) {
    const state = drawingStore.reduxStore?.store?.getState();
    const drawing = state?.drawingsById[state?.drawings.comparablesDrawingId]?.geometry;
    const bounds = mapStore.googleMap?.getBounds();
    if (/zoopla/.test(priceMode) && (drawing || bounds)) {
      setIsTooLarge(area(drawing ?? googleBoundsToGeoJsonPoly(bounds!)) > 30000000);
    } else {
      setIsTooLarge(false);
    }
  }

  const clearDrawing = () => {
    const drawingId = getComparablesDrawingId();
    drawingId && deleteDrawing(drawingId);
    setDrawing(undefined);
    setPolygonGeometry(null);
    setSearchPoly({ bounding_box_geo_filter: googleBoundsToEsBounds(mapBounds) });
  };

  useEffect(() => {
    validateDrawingSize(priceMode);
    return drawingStore.reduxStore?.store.subscribe(() => validateDrawingSize(priceMode));
  }, [priceMode, mapX, mapY, zoom]);

  useEffect(() => {
    polygonGeometry
      ? setSearchPoly({ poly_geo_filter: polygonGeometry })
      : setSearchPoly({ bounding_box_geo_filter: googleBoundsToEsBounds(mapBounds) });

    let selectedGeometry = getSelectedGeometry();

    const unsubscribe = subscribeToDrawingStore(() => {
      selectedGeometry = getSelectedGeometry();
      if (selectedGeometry?.geometry) {
        setDrawing(selectedGeometry);
        try {
          setPolygonGeometry(convertGeoJsonToPolygonGeoFilter(selectedGeometry.geometry));
        } catch (e) {
          setPolygonGeometry(null);
        }
      }
    });
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [bounds, mapBounds, polygonGeometry, startDrawing]);

  function convertHometrackSubCategoryToGQLEnum<T extends string>(str: T): T {
    return str.replace(/[-/ ]/g, "_") as T;
  }

  const results = useComparableSearch({
    search: {
      ...buildCompsQuery(currentFilters, disabledFields),
      ...searchPoly,
      sub_categories: currentFilters.dcsConditions
        ?.filter((condition) => !!condition.sub_category)
        .map((condition) => convertHometrackSubCategoryToGQLEnum(condition.sub_category as PropertySubCategoryGQL)),
    },
    sort: {
      fieldName: "date_of_transfer",
      sortType: {
        order: "desc",
      },
    },
    priceMode,
  });

  const zooplaTransactions = useMemo(
    () => results.data?.zooplaComparableSearch?.edges.map((edge) => edge.node) ?? (empty as ZooplaComparableDTO[]),
    [results?.data?.zooplaComparableSearch]
  );
  const landTechTransactions = useMemo(
    () => results.data?.comparableSearch?.edges.map((edge) => edge.node) ?? (empty as RecordType[]),
    [results?.data?.comparableSearch]
  );

  const soldTransactions = useMemo(() => {
    if (isZooplaData) {
      return {
        transactions: validValueResults(zooplaTransactions, priceMode, CalculatorResultTypes.SOLD),
        average: lastValueAverage(zooplaTransactions, priceMode, unitPreference),
      };
    }
    return {
      transactions: validPriceResults(landTechTransactions, priceMode, CalculatorResultTypes.SOLD),
      average: soldPriceAverage(landTechTransactions, priceMode, unitPreference),
    };
  }, [zooplaTransactions, landTechTransactions, isZooplaData, priceMode, unitPreference]);

  const marketTransactions = useMemo(() => {
    if (isZooplaData) {
      return {
        transactions: validValueResults(zooplaTransactions, priceMode, CalculatorResultTypes.MARKET),
        average: currentValueAverage(zooplaTransactions, priceMode, unitPreference),
      };
    }
    return {
      transactions: validPriceResults(landTechTransactions, priceMode, CalculatorResultTypes.MARKET),
      average: marketValueAverage(landTechTransactions, priceMode, unitPreference),
    };
  }, [zooplaTransactions, landTechTransactions, isZooplaData, priceMode, unitPreference]);

  const floorAreaTransactions = useMemo(() => {
    if (isZooplaData) {
      return zooplaTransactions.filter((row) => Number(row.attributes.floorArea) > 0);
    }
    return landTechTransactions.filter((row) => Number(row.total_floor_area) > 0);
  }, [zooplaTransactions, landTechTransactions, isZooplaData]);

  const csvTransactions = useMemo(() => {
    if (!incNoFloorArea) {
      return floorAreaTransactions;
    } else {
      return isZooplaData ? zooplaTransactions : landTechTransactions;
    }
  }, [floorAreaTransactions, incNoFloorArea, isZooplaData, landTechTransactions, zooplaTransactions]);

  const toggleDataSource = useCallback(() => {
    debouncedTrackControlChange({
      filterName: "dataSource",
      filterState: dataSource === "hmlr" ? "hometrack" : "hmlr",
    });
    setDataSource(dataSource === "hmlr" ? "hometrack" : "hmlr");
  }, [dataSource, debouncedTrackControlChange]);

  // Hack to ensure users off the beta don't get stuck within the hometrack data source
  useEffect(() => {
    if (!useHometrack && dataSource === "hometrack") {
      setDataSource("hmlr");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useHometrack]);

  return (
    <div className={clsx("flex flex-col gap-2", { "opacity-50": disabled })}>
      {useHometrack && <ComparablesDataSourceSwitch dataSource={dataSource} onDataSourceChange={toggleDataSource} />}
      {showUpgradeCta && <HometrackUpgradeCTA />}
      {!showUpgradeCta && (
        <>
          <Accordion type="single" collapsible={true} defaultValue="filters">
            <Accordion.Item value="filters">
              <Accordion.Header>
                <Accordion.Trigger className="w-full px-3 py-2 group flex justify-between items-center cursor-pointer bg-transparent!">
                  <h2 className="font-bold">Filters</h2>
                  <i className={clsx("icon-lt-arrow-down-s-line group-data-[state=open]:-rotate-180")} />
                </Accordion.Trigger>
              </Accordion.Header>
              <Accordion.Content className="p-2 cursor-default bg-white border border-border-action rounded flex flex-col gap-2">
                {hasFeature(Feature.comparablesPpSqftMode) && (
                  <PriceMode
                    priceType={priceType}
                    priceMode={priceMode}
                    setPriceMode={setPriceMode}
                    useZooplaOptions={useHometrack ? dataSource === "hometrack" : hasFeature(Feature.zooplaFullAccess)}
                    // Kinda hacky way to display the list in the old fashion
                    // TODO: Remove this when hometrackv2 is the default
                    showAllOptions={!useHometrack}
                    eventTracking={debouncedTrackControlChange}
                    priceSource={priceSource}
                    setPriceSource={setPriceSource}
                  />
                )}
                {hasFeature(Feature.comparablesPpSqftMode) && (
                  <PriceType
                    unitPreference={unitPreference}
                    priceType={priceType}
                    setPriceType={setPriceType}
                    eventTracking={trackControlChange}
                  />
                )}
                <FiltersWithClear
                  filters={currentFilters}
                  setFilters={setFilters}
                  setHmlrFilters={setHmlrFilters}
                  setHometrackFilters={setHometrackFilters}
                  unitPreference={unitPreference}
                  priceMode={priceMode}
                  dataSource={dataSource}
                  eventTracking={debouncedTrackControlChange}
                />
                {drawingStore.currentWorkspaceId && hasFeature(Feature.comparablesCalculator) && (
                  <Suspense
                    fallback={
                      <div className="flex flex-col p-2 gap-2">
                        <Spinner />
                        <div className="text-xs text-center text-text-base-tertiary">Loading drawing tools...</div>
                      </div>
                    }
                  >
                    <ComparablesControlPageDrawings
                      drawing={drawing}
                      clearDrawing={clearDrawing}
                      setDrawing={setDrawing}
                      setStartDrawing={setStartDrawing}
                      isTooLarge={isTooLarge}
                    />
                  </Suspense>
                )}
              </Accordion.Content>
            </Accordion.Item>
          </Accordion>
          {hasFeature(Feature.comparablesCalculator) && (
            <>
              {isTooLarge && !drawing ? (
                <ComparablesCalculatorErrorMessage className="mx-3" />
              ) : (
                <CalculatorResults
                  loading={results.loading}
                  areaSet={!!searchPoly}
                  locale={locale}
                  totalResults={isZooplaData ? zooplaTransactions.length : landTechTransactions.length}
                  floorAreaResults={floorAreaTransactions.length}
                  marketAverage={marketTransactions.average}
                  marketResults={marketTransactions.transactions.length}
                  soldAverage={soldTransactions.average}
                  soldResults={soldTransactions.transactions.length}
                  priceMode={priceMode}
                  priceType={priceType}
                  unitPreference={unitPreference}
                />
              )}
              <CalculatorActions
                loading={results.loading}
                priceMode={priceMode}
                transactions={csvTransactions}
                incNoFloorArea={incNoFloorArea}
                setIncNoFloorArea={setIncNoFloorArea}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};
